import React, { useState, useEffect, useRef } from 'react';
import { Modal, Button, Form, Spinner, Alert } from 'react-bootstrap';
import StatusIndicator from './StatusIndicator';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import './Dashboard.css';
import {
    Search, Upload, Download, RefreshCw, UserPlus, Users, X, Info, Filter,
    UserMinus,
} from 'lucide-react';
import Sidebar from './Sidebar';

// ─── FilterDropdown ───────────────────────────────────────────────────────────

const FilterDropdown = ({ categories, selected, onToggle, onSelectAll, onClearAll }) => {
    const [open, setOpen] = React.useState(false);
    const ref = React.useRef(null);

    React.useEffect(() => {
        const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    return (
        <div ref={ref} style={{ position: 'relative' }}>
            <button className="filter-dropdown-toggle" onClick={() => setOpen(o => !o)}>
                <Filter size={15} />
                Status ({selected.length})
            </button>
            {open && (
                <div style={{
                    position: 'absolute', right: 0, top: 'calc(100% + 6px)', zIndex: 200,
                    background: '#fff', border: '1px solid #e5e7eb', borderRadius: '10px',
                    padding: '12px', minWidth: '240px',
                    boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
                }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                        <button onClick={onSelectAll} style={{ background: 'none', border: 'none', color: '#1a3a28', fontSize: '12px', fontWeight: 600, cursor: 'pointer', padding: 0 }}>Select All</button>
                        <button onClick={onClearAll} style={{ background: 'none', border: 'none', color: '#dc2626', fontSize: '12px', fontWeight: 600, cursor: 'pointer', padding: 0 }}>Clear All</button>
                    </div>
                    <hr style={{ margin: '8px 0', borderColor: '#f3f4f6' }} />
                    {categories.map(cat => (
                        <label key={cat} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '5px 0', cursor: 'pointer', fontSize: '13.5px', color: '#374151' }}>
                            <input type="checkbox" checked={selected.includes(cat)} onChange={() => onToggle(cat)} />
                            {cat}
                        </label>
                    ))}
                </div>
            )}
        </div>
    );
};

// ─── UserManagement ───────────────────────────────────────────────────────────

const UserManagement = () => {
    const navigate = useNavigate();

    useEffect(() => {
        const token = localStorage.getItem('x-cc-auth');
        if (!token) {
            navigate('/');
        } else {
            fetchUsers();
        }
    }, [navigate]);

    const tableRef = useRef(null);
    const [items, setItems] = useState([]);
    const [query, setQuery] = useState('');
    const [sortOrder, setSortOrder] = useState(null); // null | 'asc' | 'desc'
    const [selectedCategories, setSelectedCategories] = useState(['Active', 'Deactivated', 'Deactivation Requested', 'Invited']);
    const categories = ['Active', 'Deactivated', 'Deactivation Requested', 'Invited'];

    const toggleCategory = (category) => {
        setSelectedCategories(prev =>
            prev.includes(category) ? prev.filter(c => c !== category) : [...prev, category]
        );
    };
    const selectAllCategories = () => setSelectedCategories([...categories]);
    const clearAllCategories = () => setSelectedCategories([]);

    const showEmpId = items.some(item => item.empId);

    const filteredData = items
        .filter((item) => {
            const matchesSearch = item.email.toLowerCase().includes(query.toLowerCase());
            const matchesCategory = selectedCategories.length === 0 || selectedCategories.includes(item.status);
            return matchesSearch && matchesCategory;
        })
        .sort((a, b) => {
            if (!sortOrder) return 0;
            const cmp = a.email.toLowerCase().localeCompare(b.email.toLowerCase());
            return sortOrder === 'asc' ? cmp : -cmp;
        });

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [updatUserSuccess, setUpdatUserSuccess] = useState('');
    const [updatUserFailure, setUpdatUserFailure] = useState('');
    const [showResultModal, setShowResultModal] = useState(false);
    const [showInfoModal, setShowInfoModal] = useState(false);

    // ── Add Users modal state ──
    const [showAddModal, setShowAddModal] = useState(false);
    const [addMode, setAddMode] = useState('single'); // 'single' | 'csv'
    const [addEmail, setAddEmail] = useState('');
    const [addEmpId, setAddEmpId] = useState('');
    const [addFile, setAddFile] = useState(null);
    const [sendWelcomeEmail, setSendWelcomeEmail] = useState(true);
    const [addLoading, setAddLoading] = useState(false);


    // ── Deactivate Users modal state ──
    const [showDeactivateModal, setShowDeactivateModal] = useState(false);
    const [deactivateMode, setDeactivateMode] = useState('single'); // 'single' | 'csv'
    const [deactivateQuery, setDeactivateQuery] = useState('');
    const [deactivateFile, setDeactivateFile] = useState(null);
    const [deactivateLoading, setDeactivateLoading] = useState(false);
    const [confirmRemoveEmail, setConfirmRemoveEmail] = useState(null);
    const [showConfirmRemove, setShowConfirmRemove] = useState(false);
    const [rowLoadingEmails, setRowLoadingEmails] = useState(new Set());

    const userEmail = localStorage.getItem('email') || '';
    const avatarInitials = userEmail ? userEmail.split('@')[0].slice(0, 2).toUpperCase() : '??';
    const isHelloFreshAdmin = (userEmail.split('@')[1] || '').includes('hellofresh');

    const fetchUsers = async ({ preserveScroll = false } = {}) => {
        const scrollTop = preserveScroll && tableRef.current ? tableRef.current.scrollTop : 0;
        try {
            setLoading(true);
            const token = localStorage.getItem('x-cc-auth');
            const response = await fetch('https://api.chefcoco.com/app/admin/all/customer', {
                headers: { 'Content-Type': 'application/json', 'X-CC-AUTH': `${token}` },
            });
            if (!response.ok) throw new Error('Failed to fetch users');
            const data = await response.json();
            setItems(data || []);
            if (preserveScroll) {
                requestAnimationFrame(() => {
                    if (tableRef.current) tableRef.current.scrollTop = scrollTop;
                });
            }
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleLogout = () => {
        localStorage.removeItem('x-cc-auth');
        window.location.href = '/';
    };

    const convertToDate = (timestamp) => {
        if (!timestamp) return '—';
        const date = new Date(timestamp);
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${month}/${day}/${date.getFullYear()}`;
    };

    // ── Add Users logic ──
    const handleAddSingle = async () => {
        if (!addEmail.trim()) return;
        setAddLoading(true);
        try {
            const token = localStorage.getItem('x-cc-auth');
            let response;
            if (isHelloFreshAdmin) {
                response = await axios.post(
                    'https://api.chefcoco.com/app/admin/hellofresh/customer',
                    { email: addEmail.trim(), empId: addEmpId.trim() },
                    { headers: { 'Content-Type': 'application/json', 'X-CC-AUTH': `${token}` } }
                );
            } else {
                response = await axios.post(
                    'https://api.chefcoco.com/app/admin/all/customer',
                    { emails: addEmail.trim() },
                    { headers: { 'Content-Type': 'application/json', 'X-CC-AUTH': `${token}` } }
                );
            }
            setUpdatUserSuccess(response.data['success'] || '');
            setUpdatUserFailure(response.data['error'] || '');
            setShowAddModal(false);
            setAddEmail('');
            setAddEmpId('');
            setShowResultModal(true);
            fetchUsers({ preserveScroll: true });
        } catch (err) {
            console.error(err);
        } finally {
            setAddLoading(false);
        }
    };

    const handleAddCSV = () => {
        if (!addFile) return;
        setAddLoading(true);
        const reader = new FileReader();
        reader.onload = async (e) => {
            const lines = e.target.result.split('\n').map(l => l.trim()).filter(l => l);
            try {
                const token = localStorage.getItem('x-cc-auth');
                if (isHelloFreshAdmin) {
                    // HF CSV format: Email,Employee ID (header row skipped)
                    const rows = lines.filter(l => l.toLowerCase().startsWith('email') ? false : true);
                    const successList = [];
                    const errorList = [];
                    for (const line of rows) {
                        const cols = line.split(',').map(c => c.trim());
                        const email = cols[0];
                        const empId = cols[1] || '';
                        if (!email || !email.includes('@')) continue;
                        try {
                            const res = await axios.post(
                                'https://api.chefcoco.com/app/admin/hellofresh/customer',
                                { email, empId },
                                { headers: { 'Content-Type': 'application/json', 'X-CC-AUTH': `${token}` } }
                            );
                            if (res.data['success']) successList.push(email);
                            else errorList.push(email);
                        } catch { errorList.push(email); }
                    }
                    setUpdatUserSuccess(successList.length ? successList.join(', ') + ' added successfully' : '');
                    setUpdatUserFailure(errorList.length ? errorList.join(', ') + ' unable to update' : '');
                } else {
                    const emails = [];
                    lines.forEach(line => {
                        line.split(',').forEach(col => {
                            const t = col.trim();
                            if (t.includes('@')) emails.push(t);
                        });
                    });
                    const response = await axios.post(
                        'https://api.chefcoco.com/app/admin/all/customer',
                        { emails: emails.join(',') },
                        { headers: { 'Content-Type': 'application/json', 'X-CC-AUTH': `${token}` } }
                    );
                    setUpdatUserSuccess(response.data['success'] || '');
                    setUpdatUserFailure(response.data['error'] || '');
                }
                setShowAddModal(false);
                setAddFile(null);
                setShowResultModal(true);
                fetchUsers({ preserveScroll: true });
            } catch (err) {
                console.error(err);
            } finally {
                setAddLoading(false);
            }
        };
        reader.readAsText(addFile);
    };

    const handleDownloadTemplate = () => {
        const csvContent = isHelloFreshAdmin
            ? 'data:text/csv;charset=utf-8,Email,Employee ID\nuser@hellofresh.com,X'
            : 'data:text/csv;charset=utf-8,Email\nexample@example.com';
        const link = document.createElement('a');
        link.setAttribute('href', encodeURI(csvContent));
        link.setAttribute('download', isHelloFreshAdmin ? 'hellofresh_template.csv' : 'template.csv');
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    // ── Deactivate logic ──
    const deactivateSearchResults = deactivateQuery.trim().length > 1
        ? items.filter(i =>
            (i.status === 'Active' || i.status === 'Invited') &&
            i.email.toLowerCase().includes(deactivateQuery.toLowerCase())
          )
        : [];

    const removeCustomer = async (email) => {
        setRowLoadingEmails(prev => new Set([...prev, email]));
        setShowConfirmRemove(false);
        setShowDeactivateModal(false);
        setDeactivateQuery('');
        try {
            const token = localStorage.getItem('x-cc-auth');
            await axios.post(
                'https://api.chefcoco.com/app/admin/all/customer/remove',
                { email },
                { headers: { 'Content-Type': 'application/json', 'X-CC-AUTH': `${token}` } }
            );
            // Update just this user's status locally — no full list refresh
            setItems(prev => prev.map(u =>
                u.email === email ? { ...u, status: 'Deactivation Requested' } : u
            ));
        } catch (err) {
            console.error(err);
        } finally {
            setRowLoadingEmails(prev => { const s = new Set(prev); s.delete(email); return s; });
        }
    };

    const handleDeactivateCSV = () => {
        if (!deactivateFile) return;
        setDeactivateLoading(true);
        const reader = new FileReader();
        reader.onload = async (e) => {
            const lines = e.target.result.split('\n');
            const emails = [];
            lines.forEach(line => {
                line.split(',').forEach(col => {
                    const t = col.trim();
                    if (t.includes('@')) emails.push(t);
                });
            });
            const token = localStorage.getItem('x-cc-auth');
            let successCount = 0;
            const failures = [];
            for (const em of emails) {
                try {
                    await axios.post(
                        'https://api.chefcoco.com/app/admin/all/customer/remove',
                        { email: em },
                        { headers: { 'Content-Type': 'application/json', 'X-CC-AUTH': `${token}` } }
                    );
                    successCount++;
                } catch {
                    failures.push(em);
                }
            }
            setDeactivateLoading(false);
            setShowDeactivateModal(false);
            setDeactivateFile(null);
            setUpdatUserSuccess(successCount > 0 ? `${successCount} user(s) successfully queued for deactivation.` : '');
            setUpdatUserFailure(failures.length > 0 ? `Failed to deactivate: ${failures.join(', ')}` : '');
            setShowResultModal(true);
            fetchUsers({ preserveScroll: true });
        };
        reader.readAsText(deactivateFile);
    };

    return (
        <div className="dashboard-container">
            <Sidebar
                currentPath="/users"
                onNavigate={(path) => navigate(path)}
                onLogout={handleLogout}
            />

            <div className="main-content">
                {/* Topbar */}
                <div className="topbar">
                    <div className="topbar-title">
                        <h2>User Management</h2>
                        <span className="topbar-badge">Account · Users</span>
                    </div>
                    <button className="avatar-btn" title={userEmail}>{avatarInitials}</button>
                </div>

                <div className="content-area">
                    {/* User Table Card */}
                    <div className="section-card">
                        <div className="section-card-header">
                            <h3 className="section-card-title">All Users</h3>
                            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                                <button className="action-btn action-btn-outline" onClick={() => { setShowAddModal(true); setAddMode('single'); setAddEmail(''); setAddFile(null); }}>
                                    <UserPlus size={15} /> Add Users
                                </button>
                                <button className="action-btn action-btn-danger" onClick={() => { setShowDeactivateModal(true); setDeactivateMode('single'); setDeactivateQuery(''); setDeactivateFile(null); }}>
                                    <UserMinus size={15} /> Deactivate Users
                                </button>
                                <button className="action-btn action-btn-primary" onClick={fetchUsers} disabled={loading}>
                                    <RefreshCw size={15} className={loading ? 'spin' : ''} /> Refresh
                                </button>
                            </div>
                        </div>

                        {/* Search + Filter */}
                        <div className="search-filter-row">
                            <div className="search-wrapper">
                                <Search size={16} />
                                <input
                                    type="text"
                                    placeholder="Search by email address..."
                                    value={query}
                                    onChange={(e) => setQuery(e.target.value)}
                                />
                            </div>
                            <FilterDropdown
                                categories={categories}
                                selected={selectedCategories}
                                onToggle={toggleCategory}
                                onSelectAll={selectAllCategories}
                                onClearAll={clearAllCategories}
                            />
                        </div>

                        {/* Table */}
                        {error ? (
                            <Alert variant="danger"><strong>Error:</strong> {error}</Alert>
                        ) : loading ? (
                            <div className="text-center py-5">
                                <Spinner animation="border" style={{ color: '#1a3a28' }} />
                                <p className="text-muted mt-3">Loading users...</p>
                            </div>
                        ) : filteredData.length === 0 ? (
                            <div className="text-center py-5">
                                <Users size={48} className="text-muted mb-3" />
                                <p className="text-muted">No users found</p>
                            </div>
                        ) : (
                            <div className="table-responsive" ref={tableRef}>
                                <table className="modern-table">
                                    <thead>
                                        <tr>
                                            <th
                                                style={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}
                                                onClick={() => setSortOrder(o => o === 'asc' ? 'desc' : o === 'desc' ? null : 'asc')}
                                            >
                                                Email Address{' '}
                                                <span style={{ opacity: sortOrder ? 1 : 0.3, fontSize: '11px' }}>
                                                    {sortOrder === 'asc' ? '▲' : sortOrder === 'desc' ? '▼' : '▲'}
                                                </span>
                                            </th>
                                            {showEmpId && <th>Employee ID</th>}
                                            <th>
                                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                                                    Status
                                                    <Info
                                                        size={14}
                                                        style={{ cursor: 'pointer', opacity: 0.5 }}
                                                        onClick={() => setShowInfoModal(true)}
                                                    />
                                                </span>
                                            </th>
                                            <th>End Date</th>
                                            <th style={{ textAlign: 'center' }}>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredData.map((item, index) => (
                                            <tr key={index}>
                                                <td><strong>{item.email}</strong></td>
                                                {showEmpId && <td>{item.empId || <span className="text-muted">—</span>}</td>}
                                                <td><StatusIndicator status={item.status} /></td>
                                                <td>
                                                    {(item.status === 'Deactivated' || item.status === 'Deactivation Requested')
                                                        ? <span className="text-muted">{convertToDate(item.endDate)}</span>
                                                        : <span className="text-muted">—</span>
                                                    }
                                                </td>
                                                <td style={{ textAlign: 'center' }}>
                                                    {(item.status === 'Active' || item.status === 'Invited') && (
                                                        <button
                                                            className="remove-btn"
                                                            disabled={rowLoadingEmails.has(item.email)}
                                                            onClick={() => { setConfirmRemoveEmail(item.email); setShowConfirmRemove(true); }}
                                                        >
                                                            {rowLoadingEmails.has(item.email)
                                                                ? <><span className="spinner-border spinner-border-sm me-1" style={{ width: 11, height: 11 }} /> Deactivating…</>
                                                                : <><UserMinus size={13} /> Deactivate</>
                                                            }
                                                        </button>
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* ── Add Users Modal ── */}
            <Modal show={showAddModal} onHide={() => !addLoading && setShowAddModal(false)} centered size="md">
                <Modal.Header closeButton className="border-0 pb-0">
                    <Modal.Title>
                        <UserPlus size={22} className="me-2" style={{ color: '#1a3a28' }} />
                        Add Users
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body className="px-4 pb-2">
                    {/* Mode Toggle */}
                    <div style={{
                        display: 'flex', background: '#f3f4f6', borderRadius: '10px',
                        padding: '4px', marginBottom: '20px',
                    }}>
                        {[
                            { value: 'single', label: 'Add Single User' },
                            { value: 'csv', label: 'Upload CSV File' },
                        ].map(opt => (
                            <button
                                key={opt.value}
                                onClick={() => setAddMode(opt.value)}
                                style={{
                                    flex: 1, padding: '8px', borderRadius: '8px', border: 'none',
                                    background: addMode === opt.value ? '#fff' : 'transparent',
                                    color: addMode === opt.value ? '#1a3a28' : '#6b7280',
                                    fontWeight: addMode === opt.value ? 700 : 500,
                                    fontSize: '13px', cursor: 'pointer',
                                    boxShadow: addMode === opt.value ? '0 1px 4px rgba(0,0,0,0.1)' : 'none',
                                    transition: 'all 0.15s',
                                }}
                            >
                                {opt.label}
                            </button>
                        ))}
                    </div>

                    {addMode === 'single' ? (
                        <>
                            <Form.Group className="mb-3">
                                <Form.Label className="text-muted small">Email Address</Form.Label>
                                <Form.Control
                                    type="email"
                                    placeholder="user@example.com"
                                    value={addEmail}
                                    onChange={(e) => setAddEmail(e.target.value)}
                                    className="form-control-modern"
                                />
                            </Form.Group>
                            {isHelloFreshAdmin && (
                                <Form.Group className="mb-3">
                                    <Form.Label className="text-muted small">Employee ID</Form.Label>
                                    <Form.Control
                                        type="text"
                                        placeholder="e.g. 3791002"
                                        value={addEmpId}
                                        onChange={(e) => setAddEmpId(e.target.value)}
                                        className="form-control-modern"
                                    />
                                </Form.Group>
                            )}
                        </>
                    ) : (
                        <Form.Group className="mb-3">
                            <Form.Label className="text-muted small">CSV File with email addresses</Form.Label>
                            <Form.Control type="file" accept=".csv" onChange={(e) => setAddFile(e.target.files[0])} />
                        </Form.Group>
                    )}

                    {/* Welcome Email Toggle */}
                    <div style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        padding: '12px 14px',
                        background: '#f9fafb',
                        borderRadius: '10px',
                        border: '1px solid #e5e7eb',
                        marginBottom: '8px',
                    }}>
                        <div>
                            <div style={{ fontSize: '13.5px', fontWeight: 600, color: '#111' }}>Send Welcome Email</div>
                            <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '2px' }}>
                                Notify users with onboarding instructions
                            </div>
                        </div>
                        <label style={{ position: 'relative', display: 'inline-block', width: '44px', height: '24px', cursor: 'pointer' }}>
                            <input
                                type="checkbox"
                                checked={sendWelcomeEmail}
                                onChange={(e) => setSendWelcomeEmail(e.target.checked)}
                                style={{ opacity: 0, width: 0, height: 0 }}
                            />
                            <span style={{
                                position: 'absolute', inset: 0,
                                background: sendWelcomeEmail ? '#1a3a28' : '#d1d5db',
                                borderRadius: '12px',
                                transition: 'background 0.2s',
                            }} />
                            <span style={{
                                position: 'absolute',
                                top: '3px',
                                left: sendWelcomeEmail ? '23px' : '3px',
                                width: '18px', height: '18px',
                                background: '#fff',
                                borderRadius: '50%',
                                transition: 'left 0.2s',
                                boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                            }} />
                        </label>
                    </div>
                </Modal.Body>
                <Modal.Footer className="border-0">
                    {addMode === 'csv' && (
                        <Button variant="outline-secondary" onClick={handleDownloadTemplate}>
                            <Download size={15} className="me-1" /> Template
                        </Button>
                    )}
                    <Button variant="secondary" onClick={() => setShowAddModal(false)} disabled={addLoading}>Cancel</Button>
                    <Button
                        style={{ background: '#1a3a28', border: 'none' }}
                        onClick={addMode === 'single' ? handleAddSingle : handleAddCSV}
                        disabled={addLoading || (addMode === 'single' ? !addEmail.trim() : !addFile)  /* empId optional for HF */}
                    >
                        {addLoading
                            ? <><span className="spinner-border spinner-border-sm me-2" />Adding...</>
                            : <><UserPlus size={15} className="me-1" /> Add Users</>
                        }
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* ── Deactivate Users Modal ── */}
            <Modal show={showDeactivateModal} onHide={() => !deactivateLoading && setShowDeactivateModal(false)} centered size="md">
                <Modal.Header closeButton className="border-0 pb-0">
                    <Modal.Title>
                        <UserMinus size={22} className="me-2" style={{ color: '#dc2626' }} />
                        Deactivate Users
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body className="px-4 pb-2">
                    {/* Mode Toggle */}
                    <div style={{
                        display: 'flex', background: '#f3f4f6', borderRadius: '10px',
                        padding: '4px', marginBottom: '20px',
                    }}>
                        {[
                            { value: 'single', label: 'Search & Remove' },
                            { value: 'csv', label: 'Upload CSV File' },
                        ].map(opt => (
                            <button
                                key={opt.value}
                                onClick={() => setDeactivateMode(opt.value)}
                                style={{
                                    flex: 1, padding: '8px', borderRadius: '8px', border: 'none',
                                    background: deactivateMode === opt.value ? '#fff' : 'transparent',
                                    color: deactivateMode === opt.value ? '#dc2626' : '#6b7280',
                                    fontWeight: deactivateMode === opt.value ? 700 : 500,
                                    fontSize: '13px', cursor: 'pointer',
                                    boxShadow: deactivateMode === opt.value ? '0 1px 4px rgba(0,0,0,0.1)' : 'none',
                                    transition: 'all 0.15s',
                                }}
                            >
                                {opt.label}
                            </button>
                        ))}
                    </div>

                    {deactivateMode === 'single' ? (
                        <>
                            <div className="search-wrapper" style={{ marginBottom: '12px' }}>
                                <Search size={16} />
                                <input
                                    type="text"
                                    placeholder="Search by email address..."
                                    value={deactivateQuery}
                                    onChange={(e) => setDeactivateQuery(e.target.value)}
                                />
                            </div>
                            {deactivateSearchResults.length > 0 && (
                                <div style={{ border: '1px solid #e5e7eb', borderRadius: '10px', overflow: 'hidden', maxHeight: '220px', overflowY: 'auto' }}>
                                    {deactivateSearchResults.map((item, i) => (
                                        <div key={i} style={{
                                            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                            padding: '10px 14px',
                                            borderBottom: i < deactivateSearchResults.length - 1 ? '1px solid #f3f4f6' : 'none',
                                        }}>
                                            <div>
                                                <div style={{ fontSize: '13.5px', fontWeight: 600, color: '#111' }}>{item.email}</div>
                                                <StatusIndicator status={item.status} />
                                            </div>
                                            <button
                                                className="remove-btn"
                                                onClick={() => { setConfirmRemoveEmail(item.email); setShowConfirmRemove(true); }}
                                            >
                                                <UserMinus size={13} /> Deactivate
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}
                            {deactivateQuery.trim().length > 1 && deactivateSearchResults.length === 0 && (
                                <p className="text-muted" style={{ fontSize: '13px', textAlign: 'center', padding: '16px 0 0' }}>No active users found.</p>
                            )}
                        </>
                    ) : (
                        <Form.Group className="mb-3">
                            <Form.Label className="text-muted small">CSV file with email addresses to deactivate</Form.Label>
                            <Form.Control type="file" accept=".csv" onChange={(e) => setDeactivateFile(e.target.files[0])} />
                        </Form.Group>
                    )}
                </Modal.Body>
                <Modal.Footer className="border-0">
                    {deactivateMode === 'csv' && (
                        <Button variant="outline-secondary" onClick={handleDownloadTemplate}>
                            <Download size={15} className="me-1" /> Template
                        </Button>
                    )}
                    <Button variant="secondary" onClick={() => setShowDeactivateModal(false)} disabled={deactivateLoading}>
                        Close
                    </Button>
                    {deactivateMode === 'csv' && (
                        <Button
                            style={{ background: '#dc2626', border: 'none' }}
                            onClick={handleDeactivateCSV}
                            disabled={deactivateLoading || !deactivateFile}
                        >
                            {deactivateLoading
                                ? <><span className="spinner-border spinner-border-sm me-2" />Processing...</>
                                : <><X size={15} className="me-1" /> Process CSV</>
                            }
                        </Button>
                    )}
                </Modal.Footer>
            </Modal>

            {/* ── Confirm Deactivate Modal ── */}
            <Modal show={showConfirmRemove} onHide={() => setShowConfirmRemove(false)} centered>
                <Modal.Header closeButton className="border-0">
                    <Modal.Title>Confirm Deactivation</Modal.Title>
                </Modal.Header>
                <Modal.Body className="px-4 pb-4">
                    <p className="mb-1">Are you sure you want to deactivate:</p>
                    <p style={{ fontWeight: 700, color: '#111', margin: 0 }}>{confirmRemoveEmail}</p>
                    <p className="text-muted mt-2 mb-0" style={{ fontSize: '13px' }}>
                        They will be removed at the earliest possible date.
                    </p>
                </Modal.Body>
                <Modal.Footer className="border-0">
                    <Button variant="secondary" onClick={() => setShowConfirmRemove(false)}>Cancel</Button>
                    <Button style={{ background: '#dc2626', border: 'none' }} onClick={() => removeCustomer(confirmRemoveEmail)}>
                        Deactivate
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* ── Result Modal ── */}
            <Modal show={showResultModal} onHide={() => setShowResultModal(false)} centered>
                <Modal.Header closeButton className="border-0">
                    <Modal.Title>Operation Result</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {updatUserSuccess && <Alert variant="success" className="mb-2">{updatUserSuccess}</Alert>}
                    {updatUserFailure && <Alert variant="danger" className="mb-0">{updatUserFailure}</Alert>}
                </Modal.Body>
                <Modal.Footer className="border-0">
                    <Button variant="secondary" onClick={() => setShowResultModal(false)}>Close</Button>
                </Modal.Footer>
            </Modal>

            {/* ── Status Info Modal ── */}
            <Modal show={showInfoModal} onHide={() => setShowInfoModal(false)} centered>
                <Modal.Header closeButton className="border-0">
                    <Modal.Title className="d-flex align-items-center">
                        <Info size={20} className="me-2 text-primary" /> Note
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body className="px-4 pb-4">
                    <p className="mb-0">
                        Accounts must be removed by the last calendar day of each month and the cancellation will be effective as of the following month.
                    </p>
                </Modal.Body>
                <Modal.Footer className="border-0">
                    <Button variant="secondary" onClick={() => setShowInfoModal(false)}>Close</Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default UserManagement;
